/**
 * webhookFailureResource — the webhook-failures operator inbox (#1417
 * convergence of the former 788-line bespoke WebhookFailuresPage), authored
 * against the resource-kit v2 model/view contracts and consumed directly as a
 * `ResourceBundle` by `ResourcePage` (#1805 slice A3b).
 *
 * The resolve/replay/delete trio rides the rowActions v2 pipeline AND the
 * #1417 `detail.actions` footer from ONE source array, so the table row cell
 * and the drawer/standalone-page footer share the same runner and busy
 * registry. Replay's six error outcomes route through the v2 `errorFeedback`
 * closed-map (off `error.code`); resolve's 409 routes to `alreadyResolved`; a
 * successful resolve moves the row out of the open inbox, so from the panel it
 * `afterAction: 'close'`s.
 *
 * The MODEL lifts to module scope; the list view and the host do not. The list
 * view carries `capabilities.select`, which is the write grant (see below), and
 * the host carries the action arrays, which capture the page's mutation
 * callbacks. So `buildWebhookFailureResource(deps, { canWrite })` survives the
 * migration unchanged in shape.
 *
 * All five cell projections moved to `../widgets.tsx`. Read its header before
 * touching them: the widget module may not reach a service, and the `csvText`
 * each widget declares is what keeps `received_at` exporting the RAW ISO string
 * while its same-typed neighbour `created_at` exports the merchant-tz rendering.
 *
 * ONE accepted behaviour change, owner-decided and recorded as the sole
 * `DELETED` entry of `docs/specs/frontend-resource-v2/02-capability-parity.md`
 * §4: `error_message_head`, `resolved` and `created_at` used to declare
 * `invisible: { detail: true }`. #1797 merged detail and form into one
 * `invisible`, and the native detail-column path has no detail-only modifier,
 * so a v2 author cannot hide a field on the detail surface alone; expressing it through group
 * membership is impossible too, because `ResourceDetailPanel` builds its
 * columns from the FULL field set and never reads `groups`/`tabs`. The three
 * therefore now appear in the drawer. The operator loses nothing: the error
 * head is already shown in full by the diagnosis tab below, and
 * `resolved` / `created_at` have their own presentation there.
 */
import { useMemo } from 'react';
import i18n from '@/i18n';
import { ApiError } from '@/lib/api';
import { ERROR_CODES } from '@lib/errorCodes';
import { ROUTES } from '@lib/routes';
import {
  AFTER_ACTION,
  type BulkActionDefinition,
  type EnumOption,
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
  type ListViewDef,
} from '@resource-kit/model';
import {
  rowAction,
  type ActionDefinition,
  type ResourceBundle,
  type DataTableParams,
} from '@resource-kit/controllers/react';
import type { ListResult } from '@resource-kit/controllers';
import { toListResult } from '@/services/list-envelope';
import { WEBHOOK_FAILURE_SORTS } from '@/services/filters.generated';
import {
  ERROR_KIND,
  MAX_BULK_DELETE,
  PROVIDER,
  WEBHOOK_FAILURE_FILTERS,
  WEBHOOK_FAILURES_WRITE_PERMISSION,
  webhookFailuresAPI,
  type WebhookFailureListItem,
  type WebhookFailureReplayResult,
} from '@/services/webhook-failures';
import { usePermissions } from '@hooks/useAuth';
import {
  useBulkDeleteWebhookFailures,
  useBulkResolveWebhookFailures,
  useDeleteWebhookFailure,
  useReplayWebhookFailure,
  useResolveWebhookFailure,
} from '../hooks/useWebhookFailures';
import { WebhookFailureDetailExtra } from '../components/WebhookFailureDetailBody';
import { replayOutcomeMessage } from './replayOutcome';

/** The write-callbacks the actions dispatch. Sourced from the page's mutation
 *  hooks (which invalidate the failures namespace + count badge on success). */
export interface WebhookFailureResourceDeps {
  resolve: (id: string) => Promise<unknown>;
  replay: (id: string) => Promise<WebhookFailureReplayResult>;
  del: (id: string) => Promise<unknown>;
  bulkResolve: (
    ids: string[],
  ) => Promise<{ resolved: string[]; alreadyResolved: string[]; notFound: string[] }>;
  bulkDelete: (ids: string[]) => Promise<{ deleted: number }>;
}

const DEFAULT_PAGE_SIZE = 50;

// Normalize the `/api/webhook-failures` list body into the `ListResult` the
// shared ResourcePage consumes: the route may answer a zero/absent `page` or
// `size`, and the framework divides `total` by the page size to page the
// table, so both are floored here (`||`, not `??` — a ZERO page or size takes
// the fallback too, same as an absent one). The pre-filled page is then
// handed to `toListResult` (#2167 PR-2), the shared kernel `size` →
// controllers' `limit` rename point (#2177) — this adapter no longer
// rebuilds the object field by field. Inbox semantics (default
// `resolved=false`, explicit-override respected) live in
// `webhookFailuresAPI.list` (#1235/#1389) — unchanged here.
async function listAdapter(params: DataTableParams): Promise<ListResult<WebhookFailureListItem>> {
  const res = await webhookFailuresAPI.list({
    page: params.page,
    size: params.limit,
    extraParams: params.extraParams,
  });
  return toListResult({
    ...res,
    page: res.page || params.page || 1,
    size: res.size || DEFAULT_PAGE_SIZE,
  });
}

/** The replay success result → a v2 feedback outcome. All three success shapes
 *  (duplicate / resolved / replayed) are a `success` toast; the six ERROR
 *  outcomes route through `errorFeedback` (`replayOutcomeMessage`). */
function replaySuccessKey(result: WebhookFailureReplayResult): string {
  if (result.resolved) {
    return result.duplicate
      ? 'webhookFailures.replay.outcome.duplicate'
      : 'webhookFailures.replay.outcome.resolved';
  }
  return 'webhookFailures.replay.outcome.replayed';
}

/**
 * The resolve/replay/delete action trio, shared verbatim by `rowActions` (the
 * table cell) and `detail.actions` (the panel footer). Same ids, same runner —
 * one busy claim per row across both surfaces.
 */
function buildActions(
  deps: WebhookFailureResourceDeps,
): ActionDefinition<WebhookFailureListItem>[] {
  return [
    rowAction<WebhookFailureListItem, void, WebhookFailureReplayResult>({
      id: 'replay',
      label: 'webhookFailures.row.replay',
      permission: WEBHOOK_FAILURES_WRITE_PERMISSION,
      onAction: (row) => deps.replay(row.id),
      // Three success shapes → one success toast; the six error outcomes are
      // mapped off `error.code`/HTTP status by `replayOutcomeMessage`.
      feedback: (result) => ({ kind: 'success', message: replaySuccessKey(result) }),
      errorFeedback: (err) => replayOutcomeMessage(i18n.t, err),
    }),
    rowAction<WebhookFailureListItem>({
      id: 'resolve',
      label: 'webhookFailures.row.resolve',
      permission: WEBHOOK_FAILURES_WRITE_PERMISSION,
      // A successful resolve moves the row out of the OPEN inbox — from the
      // panel, close it (the row is gone); the table cell just refetches.
      afterAction: AFTER_ACTION.close,
      onAction: (row) => deps.resolve(row.id),
      feedback: () => ({ kind: 'success', message: 'webhookFailures.resolve.success.single' }),
      // A 409 means another operator already resolved it.
      errorFeedback: (err) =>
        err instanceof ApiError && err.code === ERROR_CODES.CONFLICT
          ? 'webhookFailures.resolve.error.alreadyResolved'
          : err instanceof Error
            ? err.message
            : String(err),
    }),
    rowAction<WebhookFailureListItem>({
      id: 'delete',
      label: 'webhookFailures.row.delete',
      permission: WEBHOOK_FAILURES_WRITE_PERMISSION,
      intent: 'danger',
      confirm: {
        title: 'webhookFailures.delete.confirmTitle',
        description: 'webhookFailures.delete.confirmMessage',
      },
      // The row no longer exists — close the panel; the table cell refetches.
      afterAction: AFTER_ACTION.close,
      onAction: (row) => deps.del(row.id),
      feedback: () => ({ kind: 'success', message: 'webhookFailures.delete.success.body' }),
    }),
  ];
}

/** Bulk resolve + delete over the selected rows (`resourceBulkActions`). Bulk
 *  delete slices to `MAX_BULK_DELETE` so the request stays bounded. */
function buildBulkActions(
  deps: WebhookFailureResourceDeps,
): BulkActionDefinition<WebhookFailureListItem>[] {
  return [
    {
      id: 'bulk-resolve',
      label: 'webhookFailures.bulkResolve.submit',
      permission: WEBHOOK_FAILURES_WRITE_PERMISSION,
      onAction: (rows) => deps.bulkResolve(rows.map((r) => r.id)),
      formatResult: (result) => {
        const r = result as { resolved: string[]; alreadyResolved: string[]; notFound: string[] };
        return i18n.t('webhookFailures.bulkResolve.success.summary', {
          resolved: r.resolved.length,
          alreadyResolved: r.alreadyResolved.length,
          notFound: r.notFound.length,
        });
      },
    },
    {
      id: 'bulk-delete',
      label: 'webhookFailures.bulkDelete.submit',
      permission: WEBHOOK_FAILURES_WRITE_PERMISSION,
      intent: 'danger',
      // The framework bulk-confirm interpolates `{{count}}` (the selection
      // size) into both strings, so the destructive copy states exactly how
      // many rows it will remove ("Delete {{count}} failures?"). A page-local
      // selection can't exceed the page-size cap (== MAX_BULK_DELETE), so the
      // confirmed count always equals what `onAction` deletes.
      confirm: {
        title: 'webhookFailures.bulkDelete.confirmTitle',
        description: 'webhookFailures.bulkDelete.confirmMessage',
      },
      // Enforce the MAX_BULK_DELETE ceiling at the call site (belt-and-braces:
      // a page-local selection can't exceed the page-size cap of 100 today).
      onAction: (rows) => deps.bulkDelete(rows.map((r) => r.id).slice(0, MAX_BULK_DELETE)),
      formatResult: (result) =>
        i18n.t('webhookFailures.bulkDelete.success.summary', {
          count: (result as { deleted: number }).deleted,
        }),
    },
  ];
}

// ─── Model + views ───────────────────────────────────────────────────────────

const PROVIDER_OPTIONS: EnumOption[] = Object.values(PROVIDER).map((v) => ({
  value: v,
  label: `webhookFailures.provider.${v}`,
}));

const ERROR_KIND_OPTIONS: EnumOption[] = Object.values(ERROR_KIND).map((v) => ({
  value: v,
  label: `webhookFailures.errorKind.${v}`,
}));

const f = createFieldConstructors<WebhookFailureListItem>();

export const webhookFailureModel = defineModel<WebhookFailureListItem>()({
  name: 'webhookFailure',
  idField: 'id',
  // What an operator calls a row. `recName` has no consumer on this surface yet
  // (the panel header travels on the host — see
  // `packages/resource-kit-controllers/react/bundle.ts`).
  recName: 'topic',
  fields: {
    provider: f.enum({
      label: 'webhookFailures.col.provider',
      options: PROVIDER_OPTIONS,
      widget: 'webhookFailureProvider',
    }),
    topic: f.text({ label: 'webhookFailures.col.topic', widget: 'webhookFailureTopic' }),
    error_kind: f.enum({
      label: 'webhookFailures.col.errorKind',
      options: ERROR_KIND_OPTIONS,
      widget: 'webhookFailureErrorKind',
    }),
    // The cell truncates a head and appends the trace id, so a filter generated
    // off this column would answer a different question than it shows.
    error_message_head: f.text({
      label: 'webhookFailures.col.errorMessage',
      filterable: false,
      widget: 'webhookFailureErrorMessage',
    }),
    // FORENSIC (#2105): a failure row is triaged against the provider's own
    // log and against an incident timeline, both UTC. Reading a bare filter
    // day as a merchant business day would put the console and the provider
    // dashboard on different days for the same search. Same reasoning, and the
    // same declaration, as `webhookResource`.
    received_at: f.datetime({
      label: 'webhookFailures.col.receivedAt',
      widget: 'webhookFailureReceivedAt',
      instantReading: 'utc',
    }),
    // Filter-only: `resolved` is not a member of the wire row at all — it is a
    // server-side predicate the QueryBuilder offers and `WEBHOOK_FAILURE_FILTERS`
    // pins to `eq`. Hence `virtual`.
    resolved: f.virtual.boolean({ label: 'webhookFailures.col.resolved' }),
    created_at: f.datetime({ label: 'webhookFailures.col.createdAt', instantReading: 'utc' }),
  },
});

/**
 * The list view, built per-call because `capabilities.select` IS the write
 * grant: selection drives the bulk actions, which are writer-only, so a viewer
 * gets no checkbox column (a selection they could never act on).
 *
 * `capabilities.search` is deliberately absent, not `false`: the definition
 * never declared `searchable` and no `WEBHOOK_FAILURE_SEARCH` constant exists.
 * Absent and `false` are different keys downstream.
 */
function buildListView(
  canWrite: boolean,
): ListViewDef<WebhookFailureListItem, typeof webhookFailureModel.fields> {
  return defineListView(webhookFailureModel, {
    items: [
      { field: 'provider' },
      { field: 'topic' },
      { field: 'error_kind' },
      { field: 'error_message_head' },
      { field: 'received_at' },
      // Filter-only columns: the QueryBuilder offers them, the whitelist pins
      // their ops, and they carry no table column.
      { field: 'resolved', columnInvisible: true },
      { field: 'created_at', columnInvisible: true },
    ],
    capabilities: { select: canWrite },
  });
}

/**
 * No `kind: 'fields'` tab, so the native form has no field groups — the detail
 * panel lays the columns out itself, exactly as before. The one custom tab is the
 * detail body: full error head, status, trace, related-event link, and the
 * raw-payload panel. It reads everything (incl. `raw_payload`) from the panel's
 * already-fetched record — the framework `api.get` fetch — so there is NO
 * second GET of the same failure. As the only tab it is what the rail opens on,
 * so the error head stays on the first screen.
 */
export const webhookFailureFormView = defineFormView(webhookFailureModel, {
  groupDisplay: 'tab',
  children: [
    {
      kind: 'custom',
      id: 'diagnosis',
      label: 'webhookFailures.detail.diagnosis',
      render: function WebhookFailureDiagnosisTab({ record }) {
        return <WebhookFailureDetailExtra failure={record} />;
      },
    },
  ],
});

export function buildWebhookFailureResource(
  deps: WebhookFailureResourceDeps,
  options: { canWrite?: boolean } = {},
): ResourceBundle<WebhookFailureListItem, typeof webhookFailureModel.fields> {
  const actions = buildActions(deps);
  return {
    model: webhookFailureModel,
    list: buildListView(options.canWrite ?? true),
    form: webhookFailureFormView,
    host: {
      api: {
        list: listAdapter,
        get: (id) => webhookFailuresAPI.getById(id),
      },
      label: 'webhookFailures.pageTitle',
      slug: 'webhook-failures',
      listTitle: 'webhookFailures.pageTitle',
      // No resource-level `permission` map: it would gate the framework
      // create/edit/delete AFFORDANCES, but this resource exposes no
      // create/update/delete `api` (list + detail only), so those slots were
      // inert/misleading. The real gates are the route `AdminGuard` (App.tsx),
      // each action's `permission: WEBHOOK_FAILURES_WRITE_PERMISSION` (enforced
      // by the shared runner — the backend-required `webhook_failures:write`
      // key), and `capabilities.select` = the write grant.
      //
      // The whitelist pins each QueryBuilder column's ops to the
      // backend-accepted set (a superset op can never be offered) and drives
      // the dropped-filter warning. Codegen mirror of the backend
      // `FAILURE_LIST_CONFIG.filterable`.
      filterable: WEBHOOK_FAILURE_FILTERS,
      sortable: WEBHOOK_FAILURE_SORTS,
      // The inbox is operator-actionable: 5s freshness + a 30s background cadence.
      staleTime: 5_000,
      refetchInterval: 30_000,
      // NOTE: the OPEN-count badge is NOT a `listTitleAdornment` here — that
      // seam only sees the CURRENT (filtered) list total, so a provider/resolved
      // filter would make it misreport the global backlog. The badge lives in
      // the wrapper's headerActions, driven by the dedicated global-unresolved
      // `/count` query, so it always shows the true backlog (see
      // WebhookFailuresPage).
      rowActions: actions,
      resourceBulkActions: buildBulkActions(deps),
      detail: {
        surface: 'both',
        // Nested route: the list sits at /webhooks/failures, not /<slug>,
        // so declare the return route for the standalone page's
        // close-on-delete.
        listPath: ROUTES.webhooks.failures,
        title: () => i18n.t('webhookFailures.detail.title'),
        actions,
      },
    },
  };
}

/**
 * Build the webhook-failure resource in-component, wiring its actions to the
 * page's mutation callbacks. On success the single-record hooks refresh the
 * trace views only; the kit runner refreshes the failures namespace + count
 * badge, without reading a detail the action closed again (#2802).
 * Shared by the inbox wrapper and the standalone detail-page wrapper so both
 * render the SAME definition (one action source, one detail body).
 */
export function useWebhookFailureResource(): ResourceBundle<
  WebhookFailureListItem,
  typeof webhookFailureModel.fields
> {
  const permissions = usePermissions();
  const canWrite = permissions.includes(WEBHOOK_FAILURES_WRITE_PERMISSION);
  // Depend on the mutations' `mutateAsync` — a STABLE reference across renders
  // in TanStack Query v5 (the result object is recreated each render, but its
  // `mutate`/`mutateAsync`/`reset` fns are memoized). Keying the memo on the
  // whole mutation object would recreate the bundle every render (an illusory
  // memo). This memo also pins the per-call `buildListView(canWrite)` result,
  // so the shell seam sees a stable list identity until the write grant flips.
  // (#1420 copies this pattern — keep it correct.)
  const resolveAsync = useResolveWebhookFailure().mutateAsync;
  const replayAsync = useReplayWebhookFailure().mutateAsync;
  const deleteAsync = useDeleteWebhookFailure().mutateAsync;
  const bulkResolveAsync = useBulkResolveWebhookFailures().mutateAsync;
  const bulkDeleteAsync = useBulkDeleteWebhookFailures().mutateAsync;
  return useMemo(
    () =>
      buildWebhookFailureResource(
        {
          resolve: (id) => resolveAsync({ id }),
          replay: (id) => replayAsync({ id }),
          del: (id) => deleteAsync({ id }),
          bulkResolve: (ids) => bulkResolveAsync({ ids }),
          bulkDelete: (ids) => bulkDeleteAsync({ ids }),
        },
        { canWrite },
      ),
    [resolveAsync, replayAsync, deleteAsync, bulkResolveAsync, bulkDeleteAsync, canWrite],
  );
}
